import axios from 'axios';
import { API_BASE_URL } from '../../config/apiConfig';

/** Clave versionada en localStorage para evitar reutilizar cache legacy. */
export const SMARTNET_TOKEN_KEY = 'smartnet_token_v1';
const PREVIOUS_TOKEN_KEYS = ['smartnet_token', 'smartnet_token_v2'];

const normalizeToken = (value) => String(value ?? '').trim();

export const setSmartnetTokenStorage = (token) => {
  if (typeof localStorage === 'undefined') return;
  const normalized = normalizeToken(token);
  if (!normalized) return;
  localStorage.setItem(SMARTNET_TOKEN_KEY, normalized);
  for (const oldKey of PREVIOUS_TOKEN_KEYS) localStorage.removeItem(oldKey);
};

const readTokenWithMigration = () => {
  if (typeof localStorage === 'undefined') return null;
  let token = localStorage.getItem(SMARTNET_TOKEN_KEY);
  if (token) return normalizeToken(token);
  token = PREVIOUS_TOKEN_KEYS.map((k) => localStorage.getItem(k)).find(Boolean) || null;
  if (token) {
    setSmartnetTokenStorage(token);
  }
  return normalizeToken(token);
};

export const clearSmartnetTokenStorage = () => {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(SMARTNET_TOKEN_KEY);
  for (const oldKey of PREVIOUS_TOKEN_KEYS) localStorage.removeItem(oldKey);
};

const buildQuery = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.set(key, String(value));
    }
  });
  const qs = query.toString();
  return qs ? `?${qs}` : '';
};

const normalizeBaseURL = (value) => {
  const raw = String(value || '').trim().replace(/\/+$/, '');
  if (!raw) return '';
  if (raw.startsWith('/')) return raw;
  if (/^https?:\/\//i.test(raw)) return raw;
  const isLocalHostLike = /^(localhost|127\.0\.0\.1|0\.0\.0\.0)(:\d+)?$/i.test(raw);
  return `${isLocalHostLike ? 'http' : 'https'}://${raw}`;
};

const toRelativePath = (path) => String(path || '').replace(/^\/+/, '');

const wrapResponse = (res) => {
  const payload = res?.data;
  const contentType = String(
    res?.headers?.['content-type'] || res?.headers?.get?.('content-type') || 'application/json'
  );
  return {
    ok: res.status >= 200 && res.status < 300,
    status: res.status,
    statusText: res.statusText || '',
    headers: res.headers || {},
    json: async () => {
      if (payload && typeof payload === 'object') return payload;
      if (typeof payload === 'string') {
        try { return JSON.parse(payload); } catch { return {}; }
      }
      return {};
    },
    blob: async () => {
      if (payload instanceof Blob) return payload;
      const raw = typeof payload === 'string' ? payload : JSON.stringify(payload ?? {});
      return new Blob([raw], { type: contentType });
    },
  };
};

class ApiService {
  constructor(baseURL = normalizeBaseURL(API_BASE_URL)) {
    this.axiosInstance = axios.create({
      baseURL,
      withCredentials: true,
      validateStatus: () => true,
      headers: { 'Content-Type': 'application/json' },
    });

    this.axiosInstance.interceptors.request.use((config) => {
      const token = readTokenWithMigration();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });

    this.axiosInstance.interceptors.response.use((response) => {
      const url = String(response?.config?.url || '');
      const isAuthRequest =
        url.includes('/auth/login') ||
        url.includes('/auth/session') ||
        url.includes('/auth/logout');
      if (response?.status === 401 && !isAuthRequest && typeof window !== 'undefined') {
        clearSmartnetTokenStorage();
        if (window.location.pathname !== '/login') {
          window.location.assign('/login');
        }
      }
      return response;
    });
  }

  async get(path, config) {
    const res = await this.axiosInstance.get(toRelativePath(path), config);
    return wrapResponse(res);
  }

  async post(path, data, config) {
    const res = await this.axiosInstance.post(toRelativePath(path), data, config);
    return wrapResponse(res);
  }

  async put(path, data, config) {
    const res = await this.axiosInstance.put(toRelativePath(path), data, config);
    return wrapResponse(res);
  }

  async patch(path, data, config) {
    const res = await this.axiosInstance.patch(toRelativePath(path), data, config);
    return wrapResponse(res);
  }

  async delete(path, config) {
    const res = await this.axiosInstance.delete(toRelativePath(path), config);
    return wrapResponse(res);
  }

  async postFormData(path, formData, config) {
    const res = await this.axiosInstance.post(toRelativePath(path), formData, {
      ...config,
      headers: { ...config?.headers, 'Content-Type': undefined },
    });
    return wrapResponse(res);
  }
}

const configurationAPIs = new ApiService();

export { buildQuery };
export default configurationAPIs;
